// client/src/components/templates/certificados/CertificadoCanvasEditor.js
// Editor visual do certificado — renderiza o HTML dentro de um iframe
// (documento completo, com <style> próprio, igual ao que vai ser salvo de
// verdade), com o <body> do iframe em contentEditable, e uma lista de
// variáveis que podem ser arrastadas (Drag and Drop API nativa do HTML5) e
// soltas exatamente no ponto do texto onde o admin quer que apareçam —
// usa document.caretRangeFromPoint no documento DO IFRAME (não no da
// página) pra achar a posição exata do drop. Complementa o editor de HTML
// cru (CKEditor, em TemplateCertificadoForm.js) — o admin pode alternar
// entre os dois.
import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { useGerarCertificadoIA } from '../../../hooks/useTemplateCertificado';
import useAlert from '../../../hooks/useAlert';

const CertificadoCanvasEditor = ({ html, onChange, placeholders = [], isLoadingPlaceholders }) => {
    const iframeRef = useRef(null);
    const { setAlert } = useAlert();
    const gerarComIA = useGerarCertificadoIA();
    const [descricaoEstilo, setDescricaoEstilo] = useState('');
    const [draggingPlaceholder, setDraggingPlaceholder] = useState(null);

    // Escreve o HTML no iframe sempre que ele muda "de fora" (montagem
    // inicial, troca de template, ou resultado da IA) — não a cada tecla
    // digitada dentro do próprio iframe, senão o cursor pularia pro início
    // a cada sync. Começa como `null` (nunca igual a `html`) para garantir
    // que a primeira renderização sempre escreva o conteúdo inicial.
    const htmlExternoRef = useRef(null);
    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;
        if (htmlExternoRef.current === html) return;
        htmlExternoRef.current = html;

        const doc = iframe.contentDocument;
        doc.open();
        doc.write(html || '<!DOCTYPE html><html><body></body></html>');
        doc.close();
        doc.body.setAttribute('contenteditable', 'true');
        doc.body.style.cursor = 'text';
        doc.body.addEventListener('input', handleIframeChange);
        doc.body.addEventListener('drop', handleDrop);
        doc.body.addEventListener('dragover', (e) => e.preventDefault());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [html]);

    const syncHtmlFromIframe = () => {
        const iframe = iframeRef.current;
        if (!iframe?.contentDocument) return;
        const novoHtml = '<!DOCTYPE html>\n' + iframe.contentDocument.documentElement.outerHTML;
        htmlExternoRef.current = novoHtml;
        onChange(novoHtml);
    };

    const handleIframeChange = () => {
        syncHtmlFromIframe();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const texto = e.dataTransfer.getData('text/plain');
        if (!texto) return;

        const iframe = iframeRef.current;
        const doc = iframe.contentDocument;
        let range = null;

        if (doc.caretRangeFromPoint) {
            range = doc.caretRangeFromPoint(e.clientX, e.clientY);
        } else if (doc.caretPositionFromPoint) {
            const pos = doc.caretPositionFromPoint(e.clientX, e.clientY);
            if (pos) {
                range = doc.createRange();
                range.setStart(pos.offsetNode, pos.offset);
            }
        }

        if (range) {
            const textNode = doc.createTextNode(texto);
            range.insertNode(textNode);
            // Move o cursor pra depois do texto inserido.
            range.setStartAfter(textNode);
            range.collapse(true);
            const selection = doc.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
        } else {
            // Sem posição exata (navegador sem suporte) — insere no final do body.
            doc.body.appendChild(doc.createTextNode(texto));
        }

        syncHtmlFromIframe();
        setDraggingPlaceholder(null);
    };

    const handleGerarIA = () => {
        if (!descricaoEstilo.trim()) {
            setAlert('Descreva o estilo do certificado que você quer gerar.', 'warning');
            return;
        }
        gerarComIA.mutate(descricaoEstilo, {
            onSuccess: (htmlGerado) => {
                htmlExternoRef.current = null; // força reescrever o iframe mesmo se por acaso for igual
                onChange(htmlGerado);
                setAlert('Certificado gerado! Ajuste o layout arrastando as variáveis, se precisar.', 'success');
            },
        });
    };

    return (
        <div className="certificado-canvas-editor">
            <div className="certificado-canvas-editor__ia flex gap-2 items-start mb-3 p-3 bg-purple-50 rounded border border-purple-200">
                <Sparkles size={20} className="text-purple-600 mt-2 icon" />
                <div className="flex-1">
                    <textarea
                        value={descricaoEstilo}
                        onChange={(e) => setDescricaoEstilo(e.target.value)}
                        className="form-input"
                        rows={2}
                        placeholder='Descreva o estilo desejado — ex: "certificado elegante, dourado e azul marinho, com moldura decorativa clássica"'
                    />
                </div>
                <button
                    type="button"
                    onClick={handleGerarIA}
                    disabled={gerarComIA.isPending}
                    className="action-button action-button-primary"
                    style={{ whiteSpace: 'nowrap' }}
                >
                    {gerarComIA.isPending ? <Loader2 size={16} className="animate-spin icon" /> : <Sparkles size={16} className="icon" />}
                    Gerar com IA
                </button>
            </div>

            <div className="certificado-canvas-editor__body flex gap-3">
                <div className="certificado-canvas-editor__variaveis" style={{ width: '220px', flexShrink: 0 }}>
                    <p className="text-xs text-gray-500 mb-2">Arraste uma variável até o ponto do certificado onde ela deve aparecer:</p>
                    {isLoadingPlaceholders ? (
                        <Loader2 size={16} className="animate-spin" />
                    ) : (
                        <div className="space-y-1">
                            {placeholders.map((p) => (
                                <div
                                    key={p.value}
                                    draggable
                                    onDragStart={(e) => {
                                        e.dataTransfer.setData('text/plain', p.value);
                                        setDraggingPlaceholder(p.value);
                                    }}
                                    onDragEnd={() => setDraggingPlaceholder(null)}
                                    className="border rounded px-2 py-1 text-xs bg-white cursor-grab select-none"
                                    style={{ opacity: draggingPlaceholder === p.value ? 0.5 : 1 }}
                                    title={p.label}
                                >
                                    {p.value}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="certificado-canvas-editor__preview flex-1 border rounded overflow-hidden" style={{ minHeight: '480px' }}>
                    <iframe
                        ref={iframeRef}
                        title="Editor visual do certificado"
                        style={{ width: '100%', height: '480px', border: 'none', background: '#fff' }}
                    />
                </div>
            </div>
        </div>
    );
};

export default CertificadoCanvasEditor;
